import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector } from 'src/utils/Hooks';

import {
  useApplyEntityFilters,
  useRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { useMemo } from 'react';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import { safeDivide } from 'src/utils/app';
import { useLatestCumulativeReviewWeekId, useFieldLevel, type FieldLevel } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';

// Helper function to generate dynamic field names for key metrics
const generateKeyMetricFieldNames = (level: FieldLevel) => ({
  lifetimeRatingVolume: `${level}LifetimeRatingVolume` as const,
  weeklyRatingVolume: `${level}WeeklyRatingVolume` as const, // Original for volume display
  weeklyPositiveRatingVolume: `${level}PositiveWeeklyRatingVolume` as const, // For rating calculation
  weightedLifetimeRating: `weighted${level.charAt(0).toUpperCase() + level.slice(1)}LifetimeRating` as const,
  weightedWeeklyRating: `weighted${level.charAt(0).toUpperCase() + level.slice(1)}WeeklyRating` as const
});

// Weekly Rating Volume, Weekly Rating, Weekly Review Volume, Weekly Review Rating
export const useReviewKeyMetricWeekly = () => {
  const { endWeek, startWeek } = useAppSelector((state) => state.mainTimePeriod);
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const fieldLevel = useFieldLevel();
  const fieldNames = generateKeyMetricFieldNames(fieldLevel);
  const retailerId = useRetailerId();
  const reviewKeyMetricWeeklyId = 'reviewsSummaryPageKeyMetricsWeekly';

  const reviewKeyMetricWeeklyRequestBuilder = useAdvancedSearchRequestBuilder(
    reviewKeyMetricWeeklyId,
    'beacon-ratings-reviews-metrics-v2'
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');

  aggregationBuilder.addAggregationField(fieldNames.lifetimeRatingVolume, fieldNames.lifetimeRatingVolume, 'sum', false);
  aggregationBuilder.addAggregationField(fieldNames.weightedLifetimeRating, fieldNames.weightedLifetimeRating, 'sum', false);
  aggregationBuilder.addAggregationField(fieldNames.weeklyRatingVolume, fieldNames.weeklyRatingVolume, 'sum', false);
  aggregationBuilder.addAggregationField(fieldNames.weeklyPositiveRatingVolume, fieldNames.weeklyPositiveRatingVolume, 'sum', false);
  aggregationBuilder.addAggregationField('weeklyReviewVolume', 'weeklyReviewVolume', 'sum', false);
  aggregationBuilder.addAggregationField(fieldNames.weightedWeeklyRating, fieldNames.weightedWeeklyRating, 'sum', false);
  aggregationBuilder.addAggregationField('weightedWeeklyReviewRating', 'weightedWeeklyReviewRating', 'sum', false);
  aggregationBuilder.addAggregationField(
    'weeklyRating',
    'weeklyRating',
    'computed',
    false,
    `(${fieldNames.weeklyPositiveRatingVolume}_sum > 0 ? ${fieldNames.weightedWeeklyRating}_sum / ${fieldNames.weeklyPositiveRatingVolume}_sum : 0)`
  );
  aggregationBuilder.addAggregationField(
    'weeklyReviewRating',
    'weeklyReviewRating',
    'computed',
    false,
    '(weeklyReviewVolume_sum > 0 ? weightedWeeklyReviewRating_sum / weeklyReviewVolume_sum : 0)'
  );
  aggregationBuilder.addAggregationField(
    'weeklyPositiveSentimentReviewVolume',
    'weeklyPositiveSentimentReviewVolume',
    'sum',
    false
  );
  aggregationBuilder.addAggregationField(
    'weeklyNegativeSentimentReviewVolume',
    'weeklyNegativeSentimentReviewVolume',
    'sum',
    false
  );

  aggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);

  reviewKeyMetricWeeklyRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .addSortField(fieldNames.weeklyRatingVolume, fieldNames.weeklyRatingVolume, 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const reviewKeyMetricWeeklyRequest = reviewKeyMetricWeeklyRequestBuilder.build();

  const { data, isLoading: reviewKeyMetricWeeklyLoading } = useGenericAdvancedSearch({
    requestId: reviewKeyMetricWeeklyId,
    queryKeys: [reviewKeyMetricWeeklyRequest],
    requestBody: [reviewKeyMetricWeeklyRequest]
  });

  const reviewKeyMetricWeeklyData = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);


  return { reviewKeyMetricWeeklyData, reviewKeyMetricWeeklyLoading };
};

//  Weekly Positive Sentiment, Weekly Negative Sentiment => this has different query

const useWeeklySentimentKeyMetric = () => {
  const reviewKeyMetricSentimentId = 'reviewsSummaryPageKeyMetricsSentiment';
  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const { endWeek, startWeek } = useAppSelector((state) => state.mainTimePeriod);

  const weeklySentimentKeyMetricRequestBuilder = useAdvancedSearchRequestBuilder(
    reviewKeyMetricSentimentId,
    'beacon-review-metrics'
  );

  const aggregationBuilder = new AggregationBuilder('reviewSentiment');
  aggregationBuilder
    .addAggregationField('Review Sentiment', 'reviewSentiment', 'value_count', true)
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addConditionTermFilter('retailerId', [retailerId]);

  weeklySentimentKeyMetricRequestBuilder.addConditionRangeFilter('weekId', startWeek, endWeek);

  weeklySentimentKeyMetricRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setProcessDocuments(true)
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const reviewKeyMetricWeeklyRequest = weeklySentimentKeyMetricRequestBuilder.build();

  const { data, isLoading: weeklySentimentKeyMetricLoading } = useGenericAdvancedSearch({
    requestId: reviewKeyMetricSentimentId,
    queryKeys: [reviewKeyMetricWeeklyRequest],
    requestBody: [reviewKeyMetricWeeklyRequest]
  });

  const sentimentKeyMetricData = useMemo(() => {
    return data ? data.data[0].aggregations.by_reviewSentiment : null;
  }, [data]);

  return { sentimentKeyMetricData, weeklySentimentKeyMetricLoading };
};

// Cumulative Rating Volume, Cumulative Rating, Cumulative Review Volume, Cumulative Review Rating, Cumulative Positive Sentiment, and Cumulative Negative Sentiment
export const useReviewKeyMetricCumulative = () => {
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();
  const fieldLevel = useFieldLevel();
  const fieldNames = generateKeyMetricFieldNames(fieldLevel);

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const reviewKeyMetricCumulativeId = 'reviewsSummaryPageKeyMetricsCumulative';

  const reviewKeyMetricCumulativeRequestBuilder = useAdvancedSearchRequestBuilder(
    reviewKeyMetricCumulativeId,
    'beacon-ratings-reviews-metrics-v2'
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');

  aggregationBuilder.addAggregationField(fieldNames.lifetimeRatingVolume, fieldNames.lifetimeRatingVolume, 'sum', false);
  aggregationBuilder.addAggregationField(fieldNames.weightedLifetimeRating, fieldNames.weightedLifetimeRating, 'sum', false);
  aggregationBuilder.addAggregationField(
    'lifetimeRating',
    'lifetimeRating',
    'computed',
    false,
    `(${fieldNames.lifetimeRatingVolume}_sum > 0 ? ${fieldNames.weightedLifetimeRating}_sum / ${fieldNames.lifetimeRatingVolume}_sum : 0)`
  );

  aggregationBuilder.addAggregationField('lifetimeReviewVolume', 'lifetimeReviewVolume', 'sum', false);
  aggregationBuilder.addAggregationField('weightedLifetimeReviewRating', 'weightedLifetimeReviewRating', 'sum', false);

  aggregationBuilder.addAggregationField(
    'lifetimeReviewRating',
    'lifetimeReviewRating',
    'computed',
    false,
    '(lifetimeReviewVolume_sum > 0 ? weightedLifetimeReviewRating_sum / lifetimeReviewVolume_sum : 0)'
  );
  aggregationBuilder.addAggregationField(
    'lifetimePositiveSentimentReviewVolume',
    'lifetimePositiveSentimentReviewVolume',
    'sum',
    false
  );

  aggregationBuilder.addAggregationField(
    'lifetimeNegativeSentimentReviewVolume',
    'lifetimeNegativeSentimentReviewVolume',
    'sum',
    false
  );

  aggregationBuilder.addAggregationField(
    'lifetimePositiveSentiment',
    'lifetimePositiveSentiment',
    'computed',
    false,
    '(lifetimeReviewVolume_sum > 0 ? lifetimePositiveSentimentReviewVolume_sum / lifetimeReviewVolume_sum : 0)'
  );
  aggregationBuilder.addAggregationField(
    'lifetimeNegativeSentiment',
    'lifetimeNegativeSentiment',
    'computed',
    false,
    '(lifetimeReviewVolume_sum > 0 ? lifetimeNegativeSentimentReviewVolume_sum / lifetimeReviewVolume_sum : 0)'
  );

  aggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);

  reviewKeyMetricCumulativeRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    // this is the key difference from other condition range filter - both of them has to be always end week for cumulative (lifetime).
    .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .addSortField(fieldNames.lifetimeRatingVolume, fieldNames.lifetimeRatingVolume, 'sum', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const reviewKeyMetricCumulativeRequest = reviewKeyMetricCumulativeRequestBuilder.build();

  const { data, isLoading: reviewKeyMetricCumulativeLoading } = useGenericAdvancedSearch({
    requestId: reviewKeyMetricCumulativeId,
    queryKeys: [reviewKeyMetricCumulativeRequest],
    requestBody: [reviewKeyMetricCumulativeRequest]
  });

  const reviewKeyMetricCumulativeData = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);

  return { reviewKeyMetricCumulativeData, reviewKeyMetricCumulativeLoading };
};

// Review Quality Score, Word Count, Keyword Count, Keyword Popularity
export const useReviewKeyMetricQuality = () => {
  const { endWeek, startWeek } = useAppSelector((state) => state.mainTimePeriod);

  const applyEntityFilters = useApplyEntityFilters();
  const removeRetailPriceRangeFilters = useRemoveRetailPriceRangeFilters('reviews');
  const retailerId = useRetailerId();
  const reviewKeyMetricQualityId = 'reviewsSummaryPageKeyMetricsQuality';

  const reviewKeyMetricQualityRequestBuilder = useAdvancedSearchRequestBuilder(
    reviewKeyMetricQualityId,
    'beacon-review-metrics'
  );

  const aggregationBuilder = new AggregationBuilder('retailerId');

  aggregationBuilder.addAggregationField('reviewQualityScore', 'reviewQualityScore', 'avg', false);
  aggregationBuilder.addAggregationField('wordCountQualityScore', 'wordCountQualityScore', 'avg', false);
  aggregationBuilder.addAggregationField('keywordCountQualityScore', 'keywordCountQualityScore', 'avg', false);
  aggregationBuilder.addAggregationField('writingQualityScore', 'writingQualityScore', 'avg', false);

  aggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);

  reviewKeyMetricQualityRequestBuilder
    .setPageNumber(1)
    .setPageSize(10000)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .setSearchBy('parent')
    .addAggregation(aggregationBuilder.build())
    .addSortField('reviewQualityScore', 'reviewQualityScore', 'avg', false)
    .apply(applyEntityFilters)
    .apply(removeRetailPriceRangeFilters);

  const reviewKeyMetricQualityRequest = reviewKeyMetricQualityRequestBuilder.build();

  const { data, isLoading: reviewKeyMetricQualityLoading } = useGenericAdvancedSearch({
    requestId: reviewKeyMetricQualityId,
    queryKeys: [reviewKeyMetricQualityRequest],
    requestBody: [reviewKeyMetricQualityRequest]
  });

  const reviewKeyMetricQualityData = useMemo(() => {
    return data ? data.data[0].aggregations.by_retailerId : null;
  }, [data]);

  return { reviewKeyMetricQualityData, reviewKeyMetricQualityLoading };
};

export const useCombineKeyMetricsData = () => {
  const { reviewKeyMetricWeeklyData } = useReviewKeyMetricWeekly();
  const { sentimentKeyMetricData } = useWeeklySentimentKeyMetric();
  const { reviewKeyMetricCumulativeData } = useReviewKeyMetricCumulative();
  const { reviewKeyMetricQualityData } = useReviewKeyMetricQuality();
  const fieldLevel = useFieldLevel();

  const keyMetricsCardData = {};

  if (reviewKeyMetricWeeklyData) {
    const weeklyDataset = reviewKeyMetricWeeklyData[0]?.additionalValues;

    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_RATING_VOLUME] = weeklyDataset?.[`${fieldLevel}WeeklyRatingVolume_sum_value`];
    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_RATING] = weeklyDataset?.weeklyRating_computed_value;
    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_REVIEW_VOLUME] = weeklyDataset?.weeklyReviewVolume_sum_value;
    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_REVIEW_RATING] = weeklyDataset?.weeklyReviewRating_computed_value;
  }

  if (sentimentKeyMetricData) {
    const sentimentSum = sentimentKeyMetricData.reduce((acc, curr) => acc + curr.value, 0);

    const getSentimentValue = (fieldId: string) => {
      const sentiment = sentimentKeyMetricData.find((datum) => datum.fieldId === fieldId) ?? { value: 0 };
      return safeDivide(sentiment.value, sentimentSum);
    };

    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_POSITIVE_SENTIMENT] = getSentimentValue('1');
    keyMetricsCardData[RatingsAndReviewMetric.WEEKLY_NEGATIVE_SENTIMENT] = getSentimentValue('-1');
  }

  if (reviewKeyMetricCumulativeData) {
    const cumulativeDataset = reviewKeyMetricCumulativeData[0]?.additionalValues;

    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_RATING] = cumulativeDataset?.lifetimeRating_computed_value;
    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_RATING_VOLUME] = cumulativeDataset?.[`${fieldLevel}LifetimeRatingVolume_sum_value`];
    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING] = cumulativeDataset?.lifetimeReviewRating_computed_value;
    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME] = cumulativeDataset?.lifetimeReviewVolume_sum_value;
    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_POSITIVE_SENTIMENT] = cumulativeDataset?.lifetimePositiveSentiment_computed_value;
    keyMetricsCardData[RatingsAndReviewMetric.CUMULATIVE_NEGATIVE_SENTIMENT] = cumulativeDataset?.lifetimeNegativeSentiment_computed_value;
  }

  if (reviewKeyMetricQualityData) {
    const qualityDataSet = reviewKeyMetricQualityData[0]?.additionalValues;
    keyMetricsCardData[RatingsAndReviewMetric.REVIEW_QUALITY_SCORE] = qualityDataSet?.reviewQualityScore_avg_value;
    keyMetricsCardData[RatingsAndReviewMetric.WORD_COUNT] = qualityDataSet?.wordCountQualityScore_avg_value;
    keyMetricsCardData[RatingsAndReviewMetric.KEYWORD_COUNT] = qualityDataSet?.keywordCountQualityScore_avg_value;
    keyMetricsCardData[RatingsAndReviewMetric.KEYWORD_POPULARITY] = qualityDataSet?.writingQualityScore_avg_value;
  }

  return {
    keyMetricsCardData
  };
};
